import { useMemo, useRef, useState } from 'react'
import {
  countryOptions,
  normalizePostalCode,
  rulesFor,
  type AddressErrors,
  type AddressField,
} from '@/lib/address/address-rules'
import { isAddressBlank, type AddressDraft } from '@/lib/customers'
import { Combobox, type ComboboxOption } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/** The id of a field's control, for labels, errors and the save's jump to the first error. */
export const addressFieldId = (field: AddressField) => `partner-address-${field}`

/** Countries by name, found by name or ISO code ("be" is Belgium). */
const COUNTRIES: ComboboxOption[] = countryOptions().map(c => ({ value: c.code, label: c.name, keywords: [c.code] }))

/**
 * The address part of the partner form (z8nrz7epeq): the country decides the
 * rest. Street and city everywhere; a State/Province/County where the country
 * uses one (a type-to-search list where it has one, required where the
 * country requires it) and an optional Region where it does not (Limburg, BE); its
 * postal code only where it has one, named and checked the way that country
 * does — the same rules the server applies (`@/lib/address/address-rules`).
 *
 * Country and a listed state are free-text dropdowns (`Combobox`): type to
 * search by name or code, pick with the arrows, Enter or a click.
 *
 * Kept to the fields an address needs. Changing the country never loses what
 * was typed (each country's state and postal code come back with it); errors
 * appear once a field is left, or on save, never while typing; and the postal
 * code is tidied when the field is left.
 */
export function PartnerAddressFields({
  value,
  onChange,
  errors,
  showAllErrors,
}: {
  value: AddressDraft
  onChange: (next: AddressDraft) => void
  /** Every current problem with the address; shown per field once it is due. */
  errors: AddressErrors
  /** After a save attempt: show every error, left or not. */
  showAllErrors: boolean
}) {
  const rules = rulesFor(value.countryCode)
  const stateOptions = useMemo<ComboboxOption[]>(
    () => (rules.states ?? []).map(s => ({ value: s.code, label: s.name, keywords: [s.code] })),
    [rules.states],
  )
  // The address is optional: its required markers show once one is started.
  const started = !isAddressBlank(value)
  const [left, setLeft] = useState<Set<AddressField>>(new Set())
  // What was entered for each country's state and postal code, so switching
  // away and back brings it back instead of losing it.
  const perCountry = useRef(new Map<string, { state: string; postalCode: string }>())

  const set = (patch: Partial<AddressDraft>) => onChange({ ...value, ...patch })
  const leave = (field: AddressField) => setLeft(prev => (prev.has(field) ? prev : new Set(prev).add(field)))
  const errorFor = (field: AddressField) => ((showAllErrors || left.has(field)) && errors[field]) || undefined

  function changeCountry(code: string) {
    perCountry.current.set(value.countryCode, { state: value.state, postalCode: value.postalCode })
    const remembered = perCountry.current.get(code)
    set({ countryCode: code, state: remembered?.state ?? '', postalCode: remembered?.postalCode ?? '' })
    setLeft(prev => {
      const next = new Set(prev)
      next.delete('state')
      next.delete('postalCode')
      return next
    })
  }

  const field = (f: AddressField, labelText: string, control: React.ReactNode, width = 'min-w-0 flex-1') => {
    const error = errorFor(f)
    return (
      <div key={f} className={cn('flex flex-col gap-1.5', width)}>
        <Label htmlFor={addressFieldId(f)}>
          {labelText}
          {started && rules.required.has(f) && <span className='text-destructive'> *</span>}
        </Label>
        {control}
        {error && (
          <p id={`${addressFieldId(f)}-error`} className='text-xs text-destructive'>
            {error}
          </p>
        )}
      </div>
    )
  }

  const describe = (f: AddressField) => ({
    'aria-invalid': !!errorFor(f),
    'aria-describedby': errorFor(f) ? `${addressFieldId(f)}-error` : undefined,
  })

  const text = (
    f: 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'postalCode',
    labelText: string,
    autoComplete: string,
    { placeholder, width }: { placeholder?: string; width?: string } = {},
  ) =>
    field(
      f,
      labelText,
      <Input
        id={addressFieldId(f)}
        value={value[f]}
        placeholder={placeholder}
        autoComplete={autoComplete}
        {...describe(f)}
        onChange={e => set({ [f]: e.target.value })}
        onBlur={() => {
          if (f === 'postalCode') {
            const tidy = normalizePostalCode(value.countryCode, value.postalCode)
            if (tidy !== value.postalCode) set({ postalCode: tidy })
          }
          leave(f)
        }}
      />,
      width,
    )

  // City, state and postal code share one row, in the order the country writes them;
  // a country that writes no state still gets an optional Region at the end.
  const localityFields = rules.localityRows.flat()
  if (!rules.usesState) localityFields.push('state')
  const locality = localityFields.map(f => {
    if (f === 'city') return text('city', rules.labels.city, 'address-level2')
    if (f === 'postalCode') {
      return text('postalCode', rules.labels.postalCode, 'postal-code', {
        placeholder: rules.postalExamples[0],
        width: 'w-36 shrink-0',
      })
    }
    if (!rules.states) return text('state', rules.labels.state, 'address-level1', { width: 'w-44 shrink-0' })
    // Typed text that names no state is kept, and the rules say "Choose a … from the list".
    return field(
      'state',
      rules.labels.state,
      <Combobox
        id={addressFieldId('state')}
        value={value.state}
        options={stateOptions}
        freeText
        autoComplete='address-level1'
        invalid={!!errorFor('state')}
        describedBy={describe('state')['aria-describedby']}
        onChange={state => set({ state })}
        onBlur={() => leave('state')}
      />,
      'w-48 shrink-0',
    )
  })

  return (
    <div className='flex flex-col gap-4'>
      {field(
        'countryCode',
        'Country',
        <Combobox
          id={addressFieldId('countryCode')}
          value={value.countryCode}
          options={COUNTRIES}
          placeholder='Type to search countries'
          autoComplete='country-name'
          invalid={!!errorFor('countryCode')}
          describedBy={describe('countryCode')['aria-describedby']}
          onChange={code => {
            if (code !== value.countryCode) changeCountry(code)
          }}
          onBlur={() => leave('countryCode')}
        />,
      )}

      {text('addressLine1', 'Street address', 'address-line1')}
      {text('addressLine2', 'Apartment, suite, etc.', 'address-line2')}

      <div className='flex gap-3'>{locality}</div>
    </div>
  )
}
